import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FluentProvider } from '@fluentui/react-components';
import { loadTheme } from '@fluentui/react';
import { AppState } from '../redux/reducers';
import { setBlueLight, setBlueLightLevel, setThemeMode } from '../redux/actions/Theme.Actions';
import { DEFAULT_THEME_MODE, ThemeMode, themeCssVars } from './guideTokens';
import { getFluentTheme, getV8Theme } from './Themes';
import { readBlueLightLevel, readBlueLightPreference, readThemePreference } from './ThemePreference';
import { DEFAULT_BLUE_LIGHT_LEVEL, blueLightTint } from './blueLight';
import { guideControlStyleHooks } from './controlSize';
import { useUiPreferencesSync } from './UiPreferences.Sync';
import './theme.css';

const appliedVarNames = new Set<string>();

/**
 * Lleva el modo al documento: `data-theme` en `<html>` (para el CSS heredado y el Sass),
 * las variables `--ichtys-*` de la guía, `color-scheme` y el tema global de Fluent v8.
 */
export const applyThemeToDocument = (mode: ThemeMode, doc: Document = document): void => {
  const root = doc.documentElement;
  root.setAttribute('data-theme', mode);
  root.style.colorScheme = mode === 'light' ? 'light' : 'dark';
  const vars = themeCssVars(mode);
  appliedVarNames.forEach((name) => {
    if (!(name in vars)) root.style.removeProperty(name);
  });
  Object.entries(vars).forEach(([name, value]) => {
    root.style.setProperty(name, value);
    appliedVarNames.add(name);
  });
  try {
    loadTheme(getV8Theme(mode));
  } catch {
    // Fluent v8 no disponible: el resto del tema se aplica igual
  }
};

/**
 * Reducción de luz azul: marca `data-blue-light` en <html>. El filtro en sí es un velo fijo
 * (.ichtys-blue-light, theme.css) que multiplica un tono cálido sobre toda la pantalla, incluidos
 * los portales de Fluent y los iframes. No se usa `filter` en <html> porque rompe position: fixed.
 */
export const applyBlueLightToDocument = (
  enabled: boolean,
  level: number = DEFAULT_BLUE_LIGHT_LEVEL,
  doc: Document = document
): void => {
  const root = doc.documentElement;
  if (enabled) {
    root.setAttribute('data-blue-light', 'on');
    root.style.setProperty('--ichtys-blue-light-tint', blueLightTint(level));
  } else {
    root.removeAttribute('data-blue-light');
    root.style.removeProperty('--ichtys-blue-light-tint');
  }
};

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? React.useEffect : React.useLayoutEffect;

/**
 * Reemplaza al `FluentProvider theme={webLightTheme}` de la raíz: toma el modo del store (slice
 * `Theme`), carga la preferencia guardada del usuario logueado y aplica el tema de la guía.
 */
export const AppThemeProvider = ({ children }: { children?: React.ReactNode }) => {
  const dispatch = useDispatch();
  const mode = useSelector((state: AppState) => state.Theme?.mode ?? DEFAULT_THEME_MODE);
  const blueLight = useSelector((state: AppState) => state.Theme?.blueLight ?? false);
  const blueLightLevel = useSelector((state: AppState) => state.Theme?.blueLightLevel ?? DEFAULT_BLUE_LIGHT_LEVEL);
  const userId = useSelector((state: AppState) => {
    const sub = state.oidc?.user?.profile?.sub;
    return sub ? String(sub) : undefined;
  });

  // Cada usuario tiene su tema: al cambiar el usuario logueado se lee su preferencia.
  React.useEffect(() => {
    dispatch(setThemeMode(readThemePreference(userId), userId));
    dispatch(setBlueLight(readBlueLightPreference(userId), userId));
    dispatch(setBlueLightLevel(readBlueLightLevel(userId), userId));
  }, [dispatch, userId]);

  useIsomorphicLayoutEffect(() => {
    applyThemeToDocument(mode);
  }, [mode]);

  useIsomorphicLayoutEffect(() => {
    applyBlueLightToDocument(blueLight, blueLightLevel);
  }, [blueLight, blueLightLevel]);

  // Tema, luz azul e idioma viajan con la cuenta (Persons.API); lo local es la caché.
  useUiPreferencesSync();

  const theme = React.useMemo(() => getFluentTheme(mode), [mode]);

  return (
    <FluentProvider
      theme={theme}
      className="ichtysThemeRoot"
      data-theme={mode}
      customStyleHooks_unstable={guideControlStyleHooks}
    >
      {children}
      {blueLight && <div className="ichtys-blue-light" aria-hidden="true" data-testid="blue-light-filter" />}
    </FluentProvider>
  );
};

export default AppThemeProvider;
